import {
  EntityAzureGitTagsContent,
  EntityAzurePipelinesContent,
  EntityAzurePullRequestsContent,
  EntityAzureReadmeCard,
  isAzureDevOpsAvailable,
  isAzurePipelinesAvailable,
} from '@backstage-community/plugin-azure-devops';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import {
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  EntitySwitch,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isComponentType,
  isKind,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { MissingAnnotationEmptyState } from '@backstage/plugin-catalog-react';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
  EntityUserProfileCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  ExpandableNavigation,
  LightBox,
  ReportIssue,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  EntityGitlabCoverageCard,
  EntityGitlabIssuesTable,
  EntityGitlabLanguageCard,
  EntityGitlabMergeRequestStatsCard,
  EntityGitlabMergeRequestsTable,
  EntityGitlabPeopleCard,
  EntityGitlabPipelinesTable,
  EntityGitlabReadmeCard,
  EntityGitlabReleasesCard,
  isGitlabAvailable,
} from '@immobiliarelabs/backstage-plugin-gitlab';
import { Grid } from '@material-ui/core';
import {
  EntityGithubInsightsComplianceCard,
  EntityGithubInsightsContributorsCard,
  EntityGithubInsightsEnvironmentsCard,
  EntityGithubInsightsLanguagesCard,
  EntityGithubInsightsReadmeCard,
  EntityGithubInsightsReleasesCard,
  isGithubInsightsAvailable,
} from '@roadiehq/backstage-plugin-github-insights';
import {
  EntityGithubPullRequestsContent,
  PullRequestsStatsCard,
  isGithubPullRequestsAvailable,
} from '@roadiehq/backstage-plugin-github-pull-requests';
import {
  DependabotAlertsWidget,
  EntityGithubDependabotContent,
  EntitySecurityInsightsContent,
  SecurityInsightsWidget,
  isSecurityInsightsAvailable,
} from '@roadiehq/backstage-plugin-security-insights';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

import { EntityFrameworkVersionsCard } from '@internal/backstage-plugin-framework-versions';
import {
  EntityQualityScoreCard,
  EntityScoresCard,
} from '@internal/backstage-plugin-scores';
import { EditWiki } from '@internal/backstage-plugin-techdocs-addon-wiki';
import { EntityTechDocsStatsCard } from '@internal/backstage-plugin-techdocs-stats';

import {
  AZURE_DEVOPS_BUILD_DEFINITION_ANNOTATION,
  AZURE_DEVOPS_PROJECT_ANNOTATION,
  AZURE_DEVOPS_PROJECT_REPO_ANNOTATION,
  GITHUB_PROJECT_SLUG_ANNOTATION,
  GITLAB_PROJECT_ID_ANNOTATION,
} from '../../util/annotations';

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <EntitySwitch>
      <EntitySwitch.Case if={isGithubInsightsAvailable}>
        <Grid item md={6}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={6}>
          <EntityGithubInsightsReadmeCard maxHeight={350} />
        </Grid>
        <Grid item md={6}>
          <EntityGithubInsightsContributorsCard />
        </Grid>
        <Grid item md={6}>
          <EntityGithubInsightsLanguagesCard />
        </Grid>
        <Grid item md={6}>
          <EntityGithubInsightsComplianceCard />
        </Grid>
        <Grid item md={6}>
          <SecurityInsightsWidget />
        </Grid>
        <Grid item md={6}>
          <DependabotAlertsWidget />
        </Grid>
      </EntitySwitch.Case>

      <EntitySwitch.Case if={isAzureDevOpsAvailable}>
        <Grid item md={6}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={6}>
          <EntityAzureReadmeCard maxHeight={350} />
        </Grid>
      </EntitySwitch.Case>

      <EntitySwitch.Case if={isGitlabAvailable}>
        <Grid item md={6}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={6}>
          <EntityGitlabReadmeCard />
        </Grid>
        <Grid item md={6}>
          <EntityGitlabPeopleCard />
        </Grid>
        <Grid item md={6}>
          <EntityGitlabLanguageCard />
        </Grid>
        <Grid item md={6}>
          <EntityGitlabCoverageCard />
        </Grid>
      </EntitySwitch.Case>

      <EntitySwitch.Case>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8} xs={12}>
          <EntityHasSubcomponentsCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <Grid item md={6} xs={12}>
      <EntityTechDocsStatsCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityFrameworkVersionsCard />
    </Grid>
  </Grid>
);

const isCiCdAvailable = (entity: Entity) =>
  isAzurePipelinesAvailable(entity) ||
  isGithubActionsAvailable(entity) ||
  isGitlabAvailable(entity);

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isAzurePipelinesAvailable}>
      <EntityAzurePipelinesContent defaultLimit={25} />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isGitlabAvailable}>
      <EntityGitlabPipelinesTable />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <MissingAnnotationEmptyState
        annotation={[
          AZURE_DEVOPS_PROJECT_ANNOTATION,
          AZURE_DEVOPS_BUILD_DEFINITION_ANNOTATION,
          GITHUB_PROJECT_SLUG_ANNOTATION,
          GITLAB_PROJECT_ID_ANNOTATION,
        ]}
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const isPullRequestsAvailable = (entity: Entity) =>
  isGithubPullRequestsAvailable(entity) ||
  isAzureDevOpsAvailable(entity) ||
  isGitlabAvailable(entity);

const pullRequestsContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubPullRequestsAvailable}>
      <Grid container spacing={6} direction="row" alignItems="stretch">
        <Grid item md={12}>
          <EntityGithubPullRequestsContent />
        </Grid>
        <Grid item md={12}>
          <PullRequestsStatsCard />
        </Grid>
      </Grid>
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isAzureDevOpsAvailable}>
      <EntityAzurePullRequestsContent defaultLimit={25} />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isGitlabAvailable}>
      <Grid container spacing={6} direction="row" alignItems="stretch">
        <Grid item md={12}>
          <EntityGitlabMergeRequestsTable />
        </Grid>
        <Grid item md={12}>
          <EntityGitlabMergeRequestStatsCard />
        </Grid>
      </Grid>
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <MissingAnnotationEmptyState
        annotation={[
          AZURE_DEVOPS_PROJECT_REPO_ANNOTATION,
          GITHUB_PROJECT_SLUG_ANNOTATION,
          GITLAB_PROJECT_ID_ANNOTATION,
        ]}
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const isIssuesAvailable = (entity: Entity) => isGitlabAvailable(entity);

const issuesContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGitlabAvailable}>
      <EntityGitlabIssuesTable />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <MissingAnnotationEmptyState
        annotation={[GITLAB_PROJECT_ID_ANNOTATION]}
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const isReleasesAvailable = (entity: Entity) =>
  isGithubInsightsAvailable(entity) ||
  isAzureDevOpsAvailable(entity) ||
  isGitlabAvailable(entity);

const releasesContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubInsightsAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityGithubInsightsReleasesCard />
        </Grid>
        <Grid item md={6}>
          <EntityGithubInsightsEnvironmentsCard />
        </Grid>
      </Grid>
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isAzureDevOpsAvailable}>
      <EntityAzureGitTagsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isGitlabAvailable}>
      <EntityGitlabReleasesCard />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <MissingAnnotationEmptyState
        annotation={[
          AZURE_DEVOPS_PROJECT_REPO_ANNOTATION,
          GITHUB_PROJECT_SLUG_ANNOTATION,
          GITLAB_PROJECT_ID_ANNOTATION,
        ]}
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ExpandableNavigation />
      <LightBox />
      <ReportIssue />
      <Mermaid />
      <EditWiki />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const scoresContent = (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <EntityQualityScoreCard />
    </Grid>
    <Grid item xs={12}>
      <EntityScoresCard />
    </Grid>
  </Grid>
);

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isCiCdAvailable} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isPullRequestsAvailable}
      path="/pull-requests"
      title="Pull Requests"
    >
      {pullRequestsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isIssuesAvailable} path="/issues" title="Issues">
      {issuesContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isReleasesAvailable}
      path="/releases"
      title="Releases"
    >
      {releasesContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSecurityInsightsAvailable}
      path="/security-insights"
      title="Security Insights"
    >
      <EntitySecurityInsightsContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSecurityInsightsAvailable}
      path="/dependabot"
      title="Dependabot"
    >
      <EntityGithubDependabotContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/scores" title="Scores">
      {scoresContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isCiCdAvailable} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isPullRequestsAvailable}
      path="/pull-requests"
      title="Pull Requests"
    >
      {pullRequestsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isIssuesAvailable} path="/issues" title="Issues">
      {issuesContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isReleasesAvailable}
      path="/releases"
      title="Releases"
    >
      {releasesContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isKubernetesAvailable}
      path="/kubernetes"
      title="Kubernetes"
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSecurityInsightsAvailable}
      path="/security-insights"
      title="Security Insights"
    >
      <EntitySecurityInsightsContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSecurityInsightsAvailable}
      path="/dependabot"
      title="Dependabot"
    >
      <EntityGithubDependabotContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/scores" title="Scores">
      {scoresContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isCiCdAvailable} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isPullRequestsAvailable}
      path="/pull-requests"
      title="Pull Requests"
    >
      {pullRequestsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isIssuesAvailable} path="/issues" title="Issues">
      {issuesContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isReleasesAvailable}
      path="/releases"
      title="Releases"
    >
      {releasesContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isKubernetesAvailable}
      path="/kubernetes"
      title="Kubernetes"
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSecurityInsightsAvailable}
      path="/security-insights"
      title="Security Insights"
    >
      <EntitySecurityInsightsContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSecurityInsightsAvailable}
      path="/dependabot"
      title="Dependabot"
    >
      <EntityGithubDependabotContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/scores" title="Scores">
      {scoresContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>

    {/* Systems can be scored (isolved's CORE rollup attaches to system:default/isolved-core).
        The boolean annotations don't apply to Systems, so only the Quality Score is shown. */}
    <EntityLayout.Route path="/scores" title="Scores">
      <EntityQualityScoreCard />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
